/**
 * organization/utils/companyConfig.ts
 *
 * CompanyConfig — configuración a nivel empresa.
 *
 * Patrón idéntico a storeConfig.ts:
 *   - Sections tipadas (fiscal, branding, operations, security, modules, inheritance)
 *   - JSONB puro — se almacenará en businesses.config (tabla futura)
 *   - getCompanyConfig() → defaults seguros
 *   - mergeCompanyConfig() → deep merge sin borrar claves desconocidas
 *   - resolveEffectiveStoreConfig() → herencia empresa → tienda
 *
 * Herencia:
 *   Empresa define defaults.
 *   Cada tienda puede sobreescribir valores específicos de su StoreConfig.
 *   resolveEffectiveStoreConfig(companyConfig, storeConfig) aplica la herencia.
 *
 * SIN React. SIN hooks. SIN Supabase.
 */

import type { StoreConfig } from './storeConfig'

// ── Sections ──────────────────────────────────────────────────────────────────

export interface CompanyConfigFiscal {
  rfc: string
  cfdi_regime: string // régimen SAT (601 = General de Ley, etc.)
  default_tax_rate: number // % IVA (16 en México)
  tax_currency: string // 'MXN'
}

export interface CompanyConfigBranding {
  legal_name: string // razón social
  commercial_name: string // nombre comercial (CELMAXX, G-TIDE, ZL MOTO)
  logo_url: string | null
  primary_color: string // hex (brand color)
  secondary_color: string // hex
}

export interface CompanyConfigOperations {
  timezone: string // 'America/Mexico_City'
  currency: string // 'MXN'
  fiscal_year_start_month: number // 1 = enero
  country_code: string // 'MX'
}

export interface CompanyConfigSecurity {
  pin_expiry_days: number // 0 = nunca expira
  max_failed_pin_attempts: number
  session_timeout_minutes: number // 0 = nunca
  require_2fa_for_admin: boolean
  require_pin_for_sensitive_ops: boolean
}

export interface CompanyConfigModules {
  pos: boolean
  inventory: boolean
  clients: boolean
  warranties: boolean
  repairs: boolean
  reports: boolean
  multi_store: boolean
  corporate_dashboard: boolean
}

export interface CompanyConfigInheritance {
  /**
   * Si true, las tiendas heredan default_tax_rate de la empresa.
   * La tienda puede seguir sobreescribir tax_enabled (encender/apagar IVA).
   */
  inherit_fiscal: boolean
  /**
   * Si true, las políticas de seguridad (PIN, sesión) de la empresa
   * aplican a todas las tiendas y no pueden sobreescribirse.
   */
  inherit_security: boolean
  /**
   * Si true, las tiendas pueden personalizar su StoreConfig libremente.
   * Si false, StoreConfig de tienda solo acepta las secciones que la
   * empresa permite (enforcement futuro — hoy informativo).
   */
  allow_store_overrides: boolean
}

export interface CompanyConfig {
  fiscal: CompanyConfigFiscal
  branding: CompanyConfigBranding
  operations: CompanyConfigOperations
  security: CompanyConfigSecurity
  modules: CompanyConfigModules
  inheritance: CompanyConfigInheritance
}

// ── Defaults ──────────────────────────────────────────────────────────────────

const COMPANY_DEFAULTS: CompanyConfig = {
  fiscal: {
    rfc: '',
    cfdi_regime: '601',
    default_tax_rate: 16,
    tax_currency: 'MXN',
  },
  branding: {
    legal_name: '',
    commercial_name: '',
    logo_url: null,
    primary_color: '#2563eb',
    secondary_color: '#64748b',
  },
  operations: {
    timezone: 'America/Mexico_City',
    currency: 'MXN',
    fiscal_year_start_month: 1,
    country_code: 'MX',
  },
  security: {
    pin_expiry_days: 0,
    max_failed_pin_attempts: 5,
    session_timeout_minutes: 0,
    require_2fa_for_admin: false,
    require_pin_for_sensitive_ops: true,
  },
  modules: {
    pos: true,
    inventory: true,
    clients: true,
    warranties: false,
    repairs: false,
    reports: false,
    multi_store: false,
    corporate_dashboard: false,
  },
  inheritance: {
    inherit_fiscal: true,
    inherit_security: false,
    allow_store_overrides: true,
  },
}

// ── Factory ───────────────────────────────────────────────────────────────────

/**
 * Convierte JSONB crudo de businesses.config a CompanyConfig tipado con defaults seguros.
 * Equivalente a getStoreConfig() pero para empresa.
 */
export function getCompanyConfig(raw: Record<string, unknown>): CompanyConfig {
  return {
    fiscal: { ...COMPANY_DEFAULTS.fiscal, ...((raw.fiscal as object) ?? {}) },
    branding: { ...COMPANY_DEFAULTS.branding, ...((raw.branding as object) ?? {}) },
    operations: { ...COMPANY_DEFAULTS.operations, ...((raw.operations as object) ?? {}) },
    security: { ...COMPANY_DEFAULTS.security, ...((raw.security as object) ?? {}) },
    modules: { ...COMPANY_DEFAULTS.modules, ...((raw.modules as object) ?? {}) },
    inheritance: { ...COMPANY_DEFAULTS.inheritance, ...((raw.inheritance as object) ?? {}) },
  }
}

/**
 * Merge profundo para guardar en JSONB.
 * No borra claves desconocidas — preserva forward compatibility.
 * Equivalente a mergeStoreConfig() pero para empresa.
 */
export function mergeCompanyConfig(
  current: Record<string, unknown>,
  patch: Partial<CompanyConfig>
): Record<string, unknown> {
  return {
    ...current,
    ...(patch.fiscal !== undefined
      ? { fiscal: { ...((current.fiscal as object) ?? {}), ...patch.fiscal } }
      : {}),
    ...(patch.branding !== undefined
      ? { branding: { ...((current.branding as object) ?? {}), ...patch.branding } }
      : {}),
    ...(patch.operations !== undefined
      ? { operations: { ...((current.operations as object) ?? {}), ...patch.operations } }
      : {}),
    ...(patch.security !== undefined
      ? { security: { ...((current.security as object) ?? {}), ...patch.security } }
      : {}),
    ...(patch.modules !== undefined
      ? { modules: { ...((current.modules as object) ?? {}), ...patch.modules } }
      : {}),
    ...(patch.inheritance !== undefined
      ? { inheritance: { ...((current.inheritance as object) ?? {}), ...patch.inheritance } }
      : {}),
  }
}

// ── Herencia empresa → tienda ─────────────────────────────────────────────────

/**
 * Aplica la herencia de CompanyConfig sobre StoreConfig de una tienda.
 *
 * Ejemplo del brief:
 *   Empresa: default_tax_rate=16 (inherit_fiscal=true)
 *   → Todas las tiendas usan IVA 16 como base
 *   → Sucursal Puebla puede poner tax_enabled=false para desactivar IVA
 *
 * Esta función es PURA y no tiene efectos. Se conectará al flujo de
 * carga de cajero cuando exista businesses table y company_id en users.
 *
 * @param companyConfig  Config de la empresa
 * @param storeConfig    Config propia de la tienda (puede ser parcial/override)
 * @returns              Config efectiva que la tienda debe usar en el POS
 */
export function resolveEffectiveStoreConfig(
  companyConfig: CompanyConfig,
  storeConfig: StoreConfig
): StoreConfig {
  // Sin herencia activa → la tienda usa su propia config tal cual
  if (!companyConfig.inheritance.inherit_fiscal) return storeConfig

  return {
    ...storeConfig,
    fiscal: {
      ...storeConfig.fiscal,
      // La empresa define la tasa de IVA; la tienda controla si está activo
      default_tax_rate: companyConfig.fiscal.default_tax_rate,
    },
  }
}
